import React, { useState } from 'react';
import { CheckCircle2, Download, Trash2, ExternalLink, Facebook, Linkedin, Edit2, MessageSquare, ChevronDown, ChevronUp, AlertCircle, FileText, Search, User, Send, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { InitechPeopleTab } from './InitechPeopleTab';
import { InitechFinancialsTab } from './InitechFinancialsTab';
import { InitechDetailsTab } from './InitechDetailsTab';
import { InitechNotificationsTab } from './InitechNotificationsTab';
import { InitechOverviewTab } from './InitechOverviewTab';
import { InitechApplicationView } from './InitechApplicationView';
import { NotesModal } from './NotesModal';
import { ScoutAIButton } from './ScoutAIButton';
const initechNotes = [{
  id: '1',
  type: 'user' as const,
  author: 'Sarah Chen',
  timestamp: 'March 15, 2026',
  title: 'Urgent: Payment Delays Escalating',
  content: 'Customer now 45 days past due on $68K balance. Multiple attempts to contact CFO unsuccessful. Recommend immediate credit suspension and escalation to collections.'
}, {
  id: '2',
  type: 'user' as const,
  author: 'Sarah Chen',
  timestamp: 'January 22, 2026',
  title: 'Concerning Financial Trends',
  content: 'Q4 2025 financials show significant revenue decline and negative cash flow. Payment terms have extended from Net 30 to 45+ days. Monitoring closely.'
}, {
  id: '3',
  type: 'system' as const,
  timestamp: 'April 15, 2025 · 2:30 PM',
  title: 'Credit Approved',
  content: 'Credit limit set to $75,000 with Net 30 terms. Score at approval: 78 (Good Standing).'
}, {
  id: '4',
  type: 'system' as const,
  timestamp: 'April 12, 2025 · 10:15 AM',
  title: 'Application Submitted',
  content: 'Submitted by Milton Waddams on behalf of Initech Corporation.'
}, {
  id: '5',
  type: 'system' as const,
  timestamp: 'April 8, 2025 · 9:00 AM',
  title: 'Application Received',
  content: 'Credit application initiated for Initech Corporation.'
}];
export function InitechCreditApplication() {
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<null | 'approve' | 'approveWithChanges' | 'reject'>(null);
  const [decisionMade, setDecisionMade] = useState<null | 'approved' | 'approvedWithChanges' | 'rejected'>(null);
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const handleDecision = (decision: 'approved' | 'approvedWithChanges' | 'rejected') => {
    setDecisionMade(decision);
    setActiveModal(null);
  };
  return <div className="min-h-full bg-gray-50 p-6" data-id="element-1873" data-mp="119425s05ghy9">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-1874" data-mp="118c66g2n4i7g">
        {/* Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-1875" data-mp="167f9nw2z2gdb">
          <div className="flex items-center justify-between gap-4" data-id="element-1876" data-mp="2qqr51wh7yyyb">
            {/* Left: Company Info + Notes */}
            <div className="shrink-0" data-id="element-1877" data-mp="2ls7jhx6jfp6h">
              <h1 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1878" data-mp="2lt96n00tw24k">
                INITECH
              </h1>
              <div className="flex items-center gap-3 text-indigo-600 mb-2" data-id="element-1879" data-mp="2gy3e3icx7p7i">
                <ExternalLink className="w-3.5 h-3.5 cursor-pointer" data-id="element-1880" data-mp="2c136h30faeea" />
                <Facebook className="w-3.5 h-3.5 cursor-pointer" data-id="element-1881" data-mp="273bgiejc92cq" />
                <Linkedin className="w-3.5 h-3.5 cursor-pointer" data-id="element-1882" data-mp="230wtrjcjtig6" />
                <Edit2 className="w-3.5 h-3.5 cursor-pointer" data-id="element-1883" data-mp="232rjdayrlyql" />
              </div>
              <div className="space-y-0.5 text-xs text-gray-600 mb-2" data-id="element-1884" data-mp="1y23oufbbuzrm">
                <div className="flex items-center gap-1.5" data-id="element-1885" data-mp="1t3irqordgw7e">
                  <span className="font-semibold text-gray-900" data-id="element-1886" data-mp="3sfl5didbewd5">
                    Credit Manager:
                  </span>
                  <span data-id="element-1887" data-mp="3ng8kv49x21dq">Sarah Chen</span>
                  <Edit2 className="w-3 h-3 text-indigo-600 cursor-pointer" data-id="element-1888" data-mp="3nhb9t6nohtr0" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-1889" data-mp="2l3vjalizehj0">
                  <span className="font-semibold text-gray-900" data-id="element-1890" data-mp="2p4z1vfseyrnx">Added:</span>
                  <span data-id="element-1891" data-mp="2b56bxmopofh0">2/15/2026</span>
                </div>
              </div>
              <button onClick={() => setShowNotesModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded flex items-center gap-1.5 text-xs font-medium relative" data-id="element-1892" data-mp="2b7ad3wv870dl">
                <MessageSquare className="w-3 h-3" data-id="element-1893" data-mp="26639ma048ncq" />
                NOTES
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-id="element-1894" data-mp="1seoonxmnec31">
                  {initechNotes.length}
                </span>
              </button>
            </div>

            {/* Right: Risk Gauge */}
            <div className="flex flex-col items-center shrink-0 mr-4" data-id="element-1895" data-mp="1nf28bfbiwfq0">
              <div className="flex items-end gap-2 mb-1" data-id="element-1896" data-mp="1nhg09fawpbtc">
                <span className="text-xs text-gray-400 font-medium" data-id="element-1897" data-mp="13ul7xtynf63a">0</span>
                <div className="relative w-64" data-id="element-1898" data-mp="zsgg90rrgjja">
                  {/* Score label */}
                  <div className="absolute -top-6 left-[28%] -translate-x-1/2 bg-red-100 text-red-800 text-xs font-bold px-2 py-0.5 rounded" data-id="element-1899" data-mp="1drg78f2qxaim">
                    28
                  </div>
                  {/* Bar */}
                  <div className="relative w-full h-6 flex rounded-full overflow-hidden" data-id="element-1900" data-mp="1mrmgtpvbpqci">
                    <div className="w-1/3 bg-red-300" data-id="element-1901" data-mp="1irccsir3da0a"></div>
                    <div className="w-1/3 bg-yellow-300" data-id="element-1902" data-mp="1ioq3adkn4kff"></div>
                    <div className="w-1/3 bg-green-300" data-id="element-1903" data-mp="1wh62f3af7yb2"></div>
                  </div>
                  {/* Indicator line */}
                  <div className="absolute top-0 bottom-0 left-[28%] w-0.5 flex flex-col items-center" data-id="element-1904" data-mp="1rjwbtkbapgln">
                    <div className="w-[3px] h-full bg-gray-700 rounded-full" style={{
                    borderLeft: '2px dashed #374151'
                  }} data-id="element-1905" data-mp="26eapd5c3bxsx"></div>
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-medium" data-id="element-1906" data-mp="2dk4pbgjrnf9s">100</span>
              </div>
              <div className="w-64 flex justify-between px-0 mt-1" data-id="element-1907" data-mp="2ih3mk7yq82c7">
                <span className="text-xs text-red-500 font-medium pl-7" data-id="element-1908" data-mp="2n9oaisuybl7t">
                  Low
                </span>
                <span className="text-xs text-yellow-600 font-medium" data-id="element-1909" data-mp="2s7pvjjbxtulx">
                  Medium
                </span>
                <span className="text-xs text-gray-600 font-medium pr-7" data-id="element-1910" data-mp="2w89rwkoosu70">
                  High
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200 mt-4 pt-3" data-id="element-1911" data-mp="367hfe0p4y57u">
            {['OVERVIEW', 'NOTIFICATIONS', 'DETAILS', 'FINANCIALS', 'PEOPLE', 'APPLICATIONS'].map(tab => <motion.button key={tab} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={() => setActiveTab(tab)} className={`flex-1 text-center py-1.5 text-xs font-semibold transition-colors ${tab === activeTab ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1912" data-mp="14bibbnifstac">
                {tab}
              </motion.button>)}
          </div>
        </div>

        {activeTab === 'OVERVIEW' ? <InitechOverviewTab data-id="element-1913" data-mp="1d9u15nbhbn1q" /> : activeTab === 'FINANCIALS' ? <InitechFinancialsTab data-id="element-1914" data-mp="18bacvg9exz67" /> : activeTab === 'PEOPLE' ? <InitechPeopleTab data-id="element-1915" data-mp="1vzuty2grtz7" /> : activeTab === 'DETAILS' ? <InitechDetailsTab data-id="element-1916" data-mp="1tvjiv28jsow" /> : activeTab === 'NOTIFICATIONS' ? <InitechNotificationsTab data-id="element-1917" data-mp="3t0zrpta49kyw" /> : activeTab === 'APPLICATIONS' ? <InitechApplicationView data-id="element-1918" data-mp="mkizgss8y67c" /> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center text-gray-500" data-id="element-1919" data-mp="rkdpyv840trv">
            Content for {activeTab} tab coming soon.
          </div>}
      </div>

      <NotesModal isOpen={showNotesModal} onClose={() => setShowNotesModal(false)} notes={initechNotes} companyName="INITECH" data-id="element-1920" data-mp="hm7r6mhlo3r8" />

      <ScoutAIButton companyName="initech" data-id="element-1921" data-mp="15ck5qg7coyqs" />
    </div>;
}